import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DashboardLayout from "../../components/SharedLayouts/DashboardLayout";
import BackArrow2 from "../../components/BackArrow2";
import Loader from "../../components/SharedLayouts/Loader";
import CustomButton from "../../components/Buttons/Button";
import IpoStatusChip from "../../components/dashboard/ipos/IpoStatusChip";
import IpoSubscriptionsTable from "../../components/dashboard/ipos/IpoSubscriptionsTable";
import ChangeIpoStatusModal from "../../components/modal/ChangeIpoStatusModal";
import AllotIpoModal from "../../components/modal/AllotIpoModal";
import GoLiveIpoModal from "../../components/modal/GoLiveIpoModal";
import DeleteIpoModal from "../../components/modal/DeleteIpoModal";
import SubscribeIpoForUserModal from "../../components/modal/SubscribeIpoForUserModal";
import { getIpo } from "../../store/actions/ipos/getIpo";
import { getIpoSubscriptions } from "../../store/actions/ipos/getIpoSubscriptions";
import { getCurrencySymbol } from "../../Helper/getCurrencySymbol";
import {
  getIpoSubscriptionCount,
  getIpoSummary,
  IPO_ALLOTTABLE_STATUSES,
  IPO_LISTABLE_STATUSES,
} from "./ipoDetailHelpers";

const DetailItem = ({ label, value }) => (
  <div className="border border-[#EAECF0] rounded-lg p-4">
    <p className="text-xs text-[#475467] mb-1">{label}</p>
    <p className="font-[600] text-base break-words">{value ?? "-"}</p>
  </div>
);

const TextBlock = ({ title, text, bullets }) => {
  if (!text) return null;
  const lines = bullets
    ? String(text)
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
    : null;
  return (
    <div className="mb-6">
      <h2 className="font-[600] text-base mb-2">{title}</h2>
      {bullets ? (
        <ul className="list-disc pl-6 text-[#475467] text-sm border border-[#EAECF0] rounded-lg p-4 space-y-1">
          {lines.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      ) : (
        <p className="text-[#475467] text-sm border border-[#EAECF0] rounded-lg p-4 whitespace-pre-line">
          {text}
        </p>
      )}
    </div>
  );
};

const formatNumber = (value) =>
  value != null ? Number(value).toLocaleString() : "-";

const tabs = [
  { id: "subscribers", name: "Subscribers" },
  { id: "disclosures", name: "Disclosures" },
];

function IpoDetail() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [activeTab, setActiveTab] = useState("subscribers");
  const [showStatus, setShowStatus] = useState(false);
  const [showAllot, setShowAllot] = useState(false);
  const [showGoLive, setShowGoLive] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showSubscribeForUser, setShowSubscribeForUser] = useState(false);

  const ipo = useSelector((state) => state?.ipos?.ipo);
  const ipoDetail = useSelector((state) => state?.ipos?.ipoDetail);
  const loading = useSelector((state) => state?.ipos?.getIpoPending);
  const subscriptions =
    useSelector((state) => state?.ipos?.ipoSubscriptions) || [];
  const subscriptionsSummary = useSelector(
    (state) => state?.ipos?.ipoSubscriptionsSummary
  );

  useEffect(() => {
    dispatch(getIpo(id));
    dispatch(getIpoSubscriptions({ id }));
  }, [dispatch, id]);

  const currencySymbol = getCurrencySymbol(ipo?.currency);
  const money = (value) =>
    value != null
      ? `${currencySymbol} ${Number(value).toLocaleString()}`
      : "-";

  const summary = getIpoSummary(ipoDetail, subscriptionsSummary);
  const subscriptionCount = getIpoSubscriptionCount(
    ipoDetail,
    subscriptionsSummary
  );
  const hasSubscriptions = subscriptionCount > 0 || subscriptions.length > 0;

  const status = ipo?.status;
  const isListed = status === "listed" || !!ipo?.listed_at;
  const isCancelled = status === "cancelled" || status === "withdrawn";
  const canAllot = IPO_ALLOTTABLE_STATUSES.includes(status) && !isListed;
  const canGoLive = IPO_LISTABLE_STATUSES.includes(status) && !isListed;
  const canDelete = !hasSubscriptions && !isListed;
  const canChangeStatus = !isListed && !isCancelled;

  const stockName = ipo?.stock?.name || ipo?.issuer;

  return (
    <DashboardLayout>
      <div className="px-2 lg:px-8">
        <div className="flex items-center mb-8 flex-row md:items-center">
          <Link to={"/dashboard"}>
            <img src="/Assets/bread.png" alt="" className="w-8 h-8 mr-2" />
          </Link>
          <img src="/Assets/chev.png" alt="" className="w-8 h-8 mr-2" />
          <Link to="/ipos" className="font-semibold text-[#475467] text-base flex">
            IPOs
          </Link>
          <img src="/Assets/chev.png" alt="" className="w-8 h-8 md:mr-2" />
          <span className="font-semibold text-[#475467] text-base bg-[#F9FAFB] p-1 px-3 rounded-md">
            {ipo?.stock?.symbol || "Detail"}
          </span>
        </div>

        <BackArrow2 mb={20} ml={0} />

        {loading && !ipo ? (
          <Loader />
        ) : (
          <>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
              <div className="flex items-center gap-4">
                {ipo?.stock?.logo_url && (
                  <img
                    src={ipo.stock.logo_url}
                    alt={ipo?.stock?.symbol}
                    className="w-14 h-14 rounded-full object-contain border border-[#EAECF0]"
                  />
                )}
                <div>
                  <h1 className="font-semibold text-2xl">
                    {stockName} ({ipo?.stock?.symbol})
                  </h1>
                  <div className="flex flex-wrap items-center gap-2 text-[#475467] text-sm mt-1">
                    <span>{ipo?.reference}</span>
                    <span>·</span>
                    <span>{ipo?.offer_type || "IPO"}</span>
                    <span>·</span>
                    <IpoStatusChip status={status} />
                    {isListed && ipo?.shariah_stock_id && (
                      <Link
                        to={`/stocks/${ipo.shariah_stock_id}`}
                        className="text-[#159AA8] underline ml-2"
                      >
                        View stock
                      </Link>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <CustomButton
                  variant="outlined"
                  customColor="#fff"
                  children="Edit Offer"
                  width="120px"
                  height="42px"
                  borderColor="#D0D5DD"
                  color="#344054"
                  disabled={isListed}
                  onClick={() => navigate(`/ipos/edit/${id}`)}
                />
                <CustomButton
                  variant="outlined"
                  customColor="#fff"
                  children="Change Status"
                  width="140px"
                  height="42px"
                  borderColor="#D0D5DD"
                  color="#344054"
                  disabled={!canChangeStatus}
                  onClick={() => setShowStatus(true)}
                />
                <CustomButton
                  variant="contained"
                  customColor="#159AA8"
                  children="Subscribe for User"
                  width="160px"
                  height="42px"
                  color="#fff"
                  onClick={() => setShowSubscribeForUser(true)}
                />
                <CustomButton
                  variant="contained"
                  customColor="#159AA8"
                  children="Apply Allotment"
                  width="150px"
                  height="42px"
                  color="#fff"
                  disabled={!canAllot}
                  onClick={() => {
                    // The table may be filtered; the modal needs every
                    // subscription still awaiting results.
                    dispatch(getIpoSubscriptions({ id }));
                    setShowAllot(true);
                  }}
                />
                <CustomButton
                  variant="contained"
                  customColor="#067647"
                  children={isListed ? "Listed" : "Go Live"}
                  width="120px"
                  height="42px"
                  color="#fff"
                  disabled={!canGoLive}
                  onClick={() => setShowGoLive(true)}
                />
                <CustomButton
                  variant="outlined"
                  customColor="#fff"
                  children="Delete"
                  width="100px"
                  height="42px"
                  borderColor="#FDA29B"
                  color="#B42318"
                  disabled={!canDelete}
                  onClick={() => setShowDelete(true)}
                />
              </div>
            </div>

            {status === "open" && !canAllot && (
              <p className="text-xs text-[#475467] mb-4">
                Allotment becomes available once the offer is closed.
              </p>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <DetailItem label="Offer Price" value={money(ipo?.offer_price)} />
              <DetailItem
                label="Minimum Subscription"
                value={
                  ipo?.min_subscription_shares != null
                    ? `${formatNumber(ipo.min_subscription_shares)} shares (${money(
                        ipo?.min_investment_amount
                      )})`
                    : "-"
                }
              />
              <DetailItem
                label="Max per Investor"
                value={
                  ipo?.max_subscription_shares == null
                    ? "Uncapped"
                    : `${formatNumber(ipo.max_subscription_shares)} shares`
                }
              />
              <DetailItem
                label="Admin Fee"
                value={
                  ipo?.admin_fee_pct != null ? `${ipo.admin_fee_pct}%` : "-"
                }
              />
              <DetailItem
                label="Shares on Offer"
                value={
                  ipo?.total_shares_on_offer == null
                    ? "No size declared"
                    : formatNumber(ipo.total_shares_on_offer)
                }
              />
              <DetailItem
                label="Shares Remaining"
                value={
                  ipo?.shares_remaining == null
                    ? "—"
                    : formatNumber(ipo.shares_remaining)
                }
              />
              <DetailItem label="Currency" value={ipo?.currency} />
              <DetailItem label="Exchange" value={ipo?.stock?.exchange} />
              <DetailItem label="Opens" value={ipo?.offer_open_date} />
              <DetailItem label="Closes" value={ipo?.offer_close_date} />
              <DetailItem label="Allotment Date" value={ipo?.allotment_date} />
              <DetailItem label="Listing Date" value={ipo?.listing_date} />
            </div>

            {summary && (
              <div className="mb-8">
                <h2 className="font-[600] text-base mb-3">Subscription Totals</h2>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <DetailItem
                    label="Subscribers"
                    value={formatNumber(summary?.subscribers_count)}
                  />
                  <DetailItem
                    label="Subscriptions"
                    value={formatNumber(summary?.subscriptions_count)}
                  />
                  <DetailItem
                    label="Shares Requested"
                    value={formatNumber(summary?.shares_requested)}
                  />
                  <DetailItem
                    label="Subscription Amount"
                    value={money(summary?.subscription_amount)}
                  />
                  <DetailItem
                    label="Admin Fees"
                    value={money(summary?.admin_fee)}
                  />
                  <DetailItem
                    label="Total Debited"
                    value={money(summary?.total_debited)}
                  />
                  <DetailItem
                    label="Shares Allotted"
                    value={formatNumber(summary?.shares_allotted)}
                  />
                  <DetailItem
                    label="Allotted Value"
                    value={money(summary?.allotted_amount)}
                  />
                  <DetailItem
                    label="Refunded"
                    value={money(summary?.refunded_amount)}
                  />
                  <DetailItem
                    label="Awaiting Allotment"
                    value={formatNumber(summary?.awaiting_allotment)}
                  />
                </div>
              </div>
            )}

            <div className="mb-8">
              <div className="mb-4 w-full overflow-x-auto">
                <div className="w-[max-content] flex items-center border-b border-b-[#EAECF0]">
                  {tabs.map((tab) => (
                    <span
                      key={tab.id}
                      className={`${
                        activeTab === tab.id
                          ? "border-b-2 border-[#128591] text-[#128591] font-[600]"
                          : "text-gray-700"
                      } mr-6 text-base pb-2 cursor-pointer`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.name}
                    </span>
                  ))}
                </div>
              </div>

              {activeTab === "subscribers" ? (
                <IpoSubscriptionsTable ipoId={id} ipoStatus={status} />
              ) : (
                <div>
                  <TextBlock title="Overview" text={ipo?.overview} />
                  <TextBlock title="Highlights" text={ipo?.highlights} bullets />
                  <TextBlock title="Key Information" text={ipo?.key_information} />
                  <TextBlock title="Risk Disclosure" text={ipo?.risk_disclosure} />
                  <TextBlock
                    title="Allotment Disclaimer"
                    text={ipo?.allotment_disclaimer}
                  />
                  {ipo?.prospectus_url && (
                    <div className="mb-6">
                      <h2 className="font-[600] text-base mb-2">Prospectus</h2>
                      <a
                        href={ipo.prospectus_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#159AA8] underline text-sm break-all"
                      >
                        {ipo.prospectus_url}
                      </a>
                    </div>
                  )}
                  {!ipo?.overview &&
                    !ipo?.highlights &&
                    !ipo?.key_information &&
                    !ipo?.risk_disclosure && (
                      <p className="text-sm text-[#475467]">
                        No disclosure copy has been added to this offer yet.
                      </p>
                    )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ChangeIpoStatusModal
        open={showStatus}
        ipo={ipo}
        onClose={() => setShowStatus(false)}
      />
      <AllotIpoModal
        open={showAllot}
        ipo={ipo}
        subscriptions={subscriptions}
        onClose={() => setShowAllot(false)}
      />
      <GoLiveIpoModal
        open={showGoLive}
        ipo={ipo}
        onClose={() => setShowGoLive(false)}
      />
      <DeleteIpoModal
        open={showDelete}
        ipo={ipo}
        onClose={() => setShowDelete(false)}
      />
      <SubscribeIpoForUserModal
        open={showSubscribeForUser}
        ipo={ipo}
        onClose={() => setShowSubscribeForUser(false)}
      />
    </DashboardLayout>
  );
}

export default IpoDetail;
